import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';
import { Icons } from '../../../shared/helpers/icons';
import { EAssessmentSubTypes, EAssessmentTypes } from '../../../shared/models/eop_types.enum';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { HazardAnnexesService } from '../../../shared/services/hazardAnnexes.service';
import { ContactsService } from '../../../shared/services/contact.service';

@Component({
  selector: 'send-al-assessment',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    FormsModule,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './send-al-assessment.component.html',
  styleUrl: './send-al-assessment.component.css'
})
export class SendAlAssessmentComponent implements OnInit {

  @Input() assessType: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  icons = Icons;
  contactsList: IContact[] = [];
  fullContactsList: IContact[] = [];
  SUBTYPES_BY_TYPE: Record<number, number[]> = {
    [EAssessmentTypes['AL-School Grounds']]: [
      EAssessmentSubTypes["AL-School Building"],
      EAssessmentSubTypes["AL-Playground"],
      EAssessmentSubTypes["AL-School Grounds"],
      EAssessmentSubTypes["AL-Other Structures"],
    ],
    [EAssessmentTypes['AL-Classroom']]: [
      EAssessmentSubTypes["AL-Classroom"],
    ],
    [EAssessmentTypes['AL-Evacuation Routes']]: [
      EAssessmentSubTypes["AL-Evacuation Routes"]
    ],
    [EAssessmentTypes['AL-Neighborhoods']]: [
      EAssessmentSubTypes["AL-Neighborhoods"]
    ],
    [EAssessmentTypes['AL-Skills Survey & Inventory']]: [
      EAssessmentSubTypes["AL-Skills Survey & Inventory"]
    ],
  };

  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  room_number: string = '';
  emails: { email: string; contact_id?: number }[] = [];
  selectedSubTypeId: number | null = null;
  currentSubTypeIds: number[] = [];

  loading: boolean = false;
  showContactsList: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private hazardAnnexesService: HazardAnnexesService,
    private contactsService: ContactsService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadSubtypesForAssessType();
    this.loadContacts();
  }

  private loadSubtypesForAssessType(): void {
    if (!this.assessType) {
      this.currentSubTypeIds = [];
      this.selectedSubTypeId = null;
      return;
    }
    const typeId = EAssessmentTypes[this.assessType as keyof typeof EAssessmentTypes];
    this.currentSubTypeIds = this.SUBTYPES_BY_TYPE[typeId] ?? [];
    this.selectedSubTypeId = this.currentSubTypeIds.length > 0 ? this.currentSubTypeIds[0] : null;
  }
  
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loading = false;
        this.fullContactsList = response.data || [];
        // Check if user is an nside.io user
        if (this.currentUser?.email?.toLowerCase().endsWith('@nside.io')) {
          // Show full list
          this.contactsList = [...this.fullContactsList];
        } else {
          // Show only non-nside.io contacts
          this.contactsList = this.fullContactsList.filter(
            (c: any) => !c.email?.toLowerCase().endsWith('@nside.io')
          );
        }
        console.log('Contacts loaded successfully:', response);
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };  

  // helper to show readable label from enum value
  subTypeLabel(id: number): string {
    return (EAssessmentSubTypes as any)[id] as string;
  }

  isEmailSelected(email: string): boolean {
    return this.emails.some(e => e.email === email);
  }  

  // add from contacts
  addContact(contact: IContact): void {
    const trimmed = contact.email.trim();
    if (trimmed && !this.emails.find(e => e.email === trimmed)) {
      this.emails.push({ email: trimmed, contact_id: contact.id });
    }
  }

  removeEmail(index: number): void {
    this.emails.splice(index, 1);
  }

  closeModal(): void {
    this.onClose.emit();
  }

  sendAssessment(): void {
    if (this.currentBuildingId && this.assessType && this.emails.length > 0 && this.selectedSubTypeId !== null) {
      this.loading = true;
      console.log('Sending to:', this.emails);
      if (this.assessType === 'AL-Classroom') {
        this.hazardAnnexesService.sendClassroomAssessment(this.currentBuildingId, this.room_number, this.emails).subscribe({
          next: (response) => {
            if (response.has_error) {
              this.loading = false;
              this.onSave.emit({ type: 'unsuccess', message: response.message });
            } else {;
              this.onSave.emit({ type: 'success', message: `This occurred after sending ${this.assessType} Assessment.` });
            }
          },
          error: (error) => {
            this.loading = false;
            console.error('Error:', error.message);
            this.onSave.emit({type: 'unsuccess', 'message': error.message});
          }
        })
      } else {        
        this.hazardAnnexesService.sendAssessmentRequest(this.currentBuildingId, String(EAssessmentTypes[this.assessType as keyof typeof EAssessmentTypes]), String(this.selectedSubTypeId), this.emails).subscribe({
          next: (response) => {
            if (response.has_error) {
              this.loading = false;
              this.onSave.emit({ type: 'unsuccess', message: response.message });
            } else {;
              this.onSave.emit({ type: 'success', message: `This occurred after sending ${(this.assessType || '').replace('AL-', '')} Assessment.` });
            }
          },
          error: (error) => {
            this.loading = false;
            console.error('Error:', error.message);
            this.onSave.emit({type: 'unsuccess', 'message': error.message});
          }
        });
      }
    }
  }

}
